import { Upload as UploadIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    DialogActions,
    DialogContent,
    Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { asyncForEach, formatAppErrorType } from "@bujus/common";
import { useApiServiceContext, useDialogDisplayer } from "@bujus/common-frontend";

import { CsvDataTable, ErrorDialog } from "@/base";
import { useSelectedEventStateContext } from "@/contexts";
import { EmergencyImportMode } from "@/enums";
import { useSnackbarsController } from "@/hooks";

const EmergencyImportDialogImportStepContent = ({
    modeSelectionFormData,
    fileUploadFormData,
    onNext,
    onPrevious,
}) => {
    const snackbarsController = useSnackbarsController();

    const apiService = useApiServiceContext();
    const [selectedEvent] = useSelectedEventStateContext();

    const [importedData, setImportedData] = useState(undefined);

    const errorDialog = useDialogDisplayer(ErrorDialog);

    const participantsQuery = useQuery({
        queryKey: ["participants", selectedEvent.id],
        queryFn: () =>
            apiService.listParticipants({
                eventId: selectedEvent.id,
                limit: 10000,
                offset: 0,
            }),
        enabled: modeSelectionFormData.importMode === EmergencyImportMode.BY_PARTICIPANT_NAME,
    });

    const updateResultsMutation = useMutation({
        mutationFn: async ({ results }) => {
            await asyncForEach(results, async (x) => {
                try {
                    await apiService.updateResult({
                        eventId: selectedEvent.id,
                        disciplineId: x.disciplineId,
                        participantId: x.participantId,
                        value: x.value,
                        attempts: x.attempts,
                    });
                } catch (error) {
                    if (error.response?.data?.type !== "ResultAlreadyExistsError") {
                        throw error;
                    }
                }
            });
        },
        onSuccess: () => {
            snackbarsController.push({
                key: `emergency-import-success-${Date.now()}`,
                message: "Ergebnisse wurden erfolgreich importiert",
                variant: "success",
            });
            onNext({
                results: importedData.filter((x) => x.participantId).length,
                skipped: importedData.filter((x) => !x.participantId).length,
            });
        },
        onError: (error) => {
            errorDialog.display({ error });
        },
    });

    useEffect(() => {
        if (fileUploadFormData?.files?.[0]) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = JSON.parse(e.target.result);
                    setImportedData(data.results || []);
                } catch (error) {
                    errorDialog.display({
                        error: {
                            response: {
                                data: {
                                    type: "InvalidFileFormatError",
                                    message: "Die Datei konnte nicht gelesen werden",
                                },
                            },
                        },
                    });
                }
            };
            reader.readAsText(fileUploadFormData.files[0]);
        }
    }, [fileUploadFormData, errorDialog]);

    const handleImportButtonClick = async () => {
        if (!importedData) return;

        let processedResults = importedData;

        if (modeSelectionFormData.importMode === EmergencyImportMode.BY_PARTICIPANT_NAME) {
            if (!participantsQuery.data) return;

            processedResults = importedData.map((x) => {
                const participant = participantsQuery.data.items.find((participant) => {
                    if (x.schoolInternalId && participant.schoolInternalId === x.schoolInternalId) {
                        return true;
                    }

                    const fullName = `${participant.name.first} ${participant.name.last}`.toLowerCase();
                    const importName = x.name?.toLowerCase();

                    if (!importName) return false;

                    if (fullName === importName) return true;

                    const nameParts = importName.split(" ");
                    return nameParts.every((part) => fullName.includes(part));
                });

                return {
                    ...x,
                    participantId: participant?.id || null,
                };
            });
        }

        await updateResultsMutation.mutateAsync({
            results: processedResults.filter((x) => x.participantId),
        });
    };

    const handlePreviousButtonClick = () => {
        onPrevious();
    };

    const isLoading =
        updateResultsMutation.isPending ||
        (modeSelectionFormData.importMode === EmergencyImportMode.BY_PARTICIPANT_NAME &&
            participantsQuery.isLoading);

    return (
        <>
            <DialogContent sx={{ pt: 3 }}>
                {importedData && (
                    <>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <AlertTitle>Importvorschau</AlertTitle>
                            {importedData.length} Ergebnisse gefunden
                            {modeSelectionFormData.importMode === EmergencyImportMode.BY_PARTICIPANT_NAME &&
                                " - Teilnehmer werden beim Import zugeordnet"}
                        </Alert>
                        <Card variant="outlined">
                            <CardContent>
                                <CsvDataTable
                                    columns={[
                                        { field: "participantId", label: "Teilnehmer-ID" },
                                        { field: "name", label: "Name" },
                                        { field: "disciplineId", label: "Disziplin" },
                                        { field: "value", label: "Wert" },
                                    ]}
                                    data={importedData.slice(0, 10)}
                                />
                                {importedData.length > 10 && (
                                    <Typography variant="body2" sx={{ mt: 2, textAlign: "center" }}>
                                        ... und {importedData.length - 10} weitere Ergebnisse
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
                {!importedData && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <Typography variant="body1">Datei wird gelesen...</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handlePreviousButtonClick} disabled={isLoading}>
                    Zurück
                </Button>
                <LoadingButton
                    onClick={handleImportButtonClick}
                    variant="contained"
                    loading={isLoading}
                    disabled={!importedData}
                    startIcon={<UploadIcon />}
                >
                    Importieren
                </LoadingButton>
            </DialogActions>
        </>
    );
};

export { EmergencyImportDialogImportStepContent };